/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios'
import { validateAndFilterRoutingTables } from "../../../../helpers/cloudPropagationValidation";
import {
  buildNexthopSettingsPayload,
  type NexthopRetryFormValues,
  type NexthopRetryPayload,
} from "../../../../helpers/cloudPropagationHelpers";
import {
  buildOutboundRuleItemPayload,
  type OutboundRuleItemPayload,
} from "../../request/request";

const API_URL = process.env.REACT_APP_API_URL || "";

// VRF Configuration interface for cluster mode
// Interface for VRF form data (from UI)
export interface VRFFormData extends NexthopRetryFormValues {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[];
  enis: string[];
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection for Telco mode
  inboundId?: number | null; // Add inboundId for Telco mode updates
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down
}

export interface VRFClusterConfig {
  vrfName: string;
  routeTargets: string;
  routingTableType: "dynamic" | "specific";
  routingTagName?: string;
  routingTagValue?: string;
  routingTables?: string[];
  nextHopType: "TAG" | "ENI" | "TGW" | "DYNAMIC_ENI_RESOLVER";
  nextHopTagName?: string;
  nextHopTagValue?: string;
  eni?: string; // Only when nextHopType is "ENI"
  tgw?: string; // Only when nextHopType is "TGW"
  mode: "TELCO" | "ENTERPRISE";
  vpcId?: string; // Add vpcId field for each VRF
  inboundId?: number | null; // Add inboundId for Telco mode updates
  persistence?: boolean; // Hold last-known route in the cloud when the peer goes down
  // Next-hop resolution retry; omitted for reflectors below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
  nexthopRetry?: NexthopRetryPayload;
}

// Cluster Propagation Rule Payload Interfaces
export interface InboundPropagationRuleClusterPayload {
  propagationRuleId?: string; // UUID for UPDATE (omit for CREATE)
  name?: string; // Display name for the rule
  // Mode
  mode?: "TELCO" | "ENTERPRISE";
  
  // For ENTERPRISE updates (single inbound)
  inboundId?: number | null;
  
  // Enterprise Dynamic Routing
  routingTableType?: "dynamic" | "specific";
  routingTagName?: string; // Note: API uses routingTagName (not routingTableTagName)
  routingTagValue?: string; // Note: API uses routingTagValue (not routingTableTagValue)
  routingTables?: string[];
  
  // Next Hop Configuration
  nextHopType?: "TAG" | "ENI" | "TGW" | "DYNAMIC_ENI_RESOLVER";
  nextHopTagName?: string;
  nextHopTagValue?: string;
  eni?: string; // Only when nextHopType is "ENI"
  tgw?: string; // Only when nextHopType is "TGW"
  
  // Telco Mode - Single VRF (backward compatibility)
  vrfName?: string;
  routeTargets?: string;
  
  // Telco Mode - Multiple VRFs
  vrfs?: VRFClusterConfig[];
  
  // VPC Selection for both Enterprise and Telco modes
  vpcId?: string;

  // Route persistence: hold last-known route in the cloud when the peer goes down (ENTERPRISE flat payload)
  persistence?: boolean;

  // Next-hop resolution retry; omitted for reflectors below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
  nexthopRetry?: NexthopRetryPayload;

  // BGP Neighbor IDs array - same format as standalone (required for cluster CREATE)
  bgpNeighborIds?: number[];
}

export interface OutboundPropagationRulesClusterPayload {
  propagationRuleId?: string;
  /** PropagationRule display name (top-level; distinct from each outbound's `name`) */
  name?: string;
  mode?: "TELCO" | "ENTERPRISE";
  bgpNeighborIds?: number[];
  outbounds: OutboundRuleItemPayload[];
}

/** @deprecated Legacy flat single-rule payload — use OutboundPropagationRulesClusterPayload */
export interface OutboundPropagationRuleClusterPayload {
  propagationRuleId?: string;
  name?: string;
  mode?: "TELCO" | "ENTERPRISE";
  subnets?: string[];
  bgpCommunity?: string;
  tagName?: string;
  tagValue?: string;
  vpcId?: string;
  nexthopIPAddress?: string;
  bgpNeighborIds?: number[];
  subnetConfigType?: "manual" | "subnet_tags" | "routing_table";
  outboundSubnetTags?: { tagName: string; tagValue: string }[];
  outboundRoutingTableTags?: { tagName: string; tagValue: string }[];
  filterSubnetsWithinRoutingTables?: boolean;
}

// Helper functions to build cluster payloads
export const buildInboundPropagationRuleClusterPayload = (
  values: any,
  mode: string,
  bgpNeighborIds?: number[],
  propagationRuleId?: string
): InboundPropagationRuleClusterPayload => {
  // Normalize mode to uppercase for API consistency
  const normalizedMode = mode?.toUpperCase() === "TELCO" ? "TELCO" : "ENTERPRISE";
  const isTelco = normalizedMode === "TELCO";
  const addRuleId = (p: InboundPropagationRuleClusterPayload) => {
    if (propagationRuleId) p.propagationRuleId = propagationRuleId;
    if (values.name != null && String(values.name).trim() !== '') p.name = String(values.name).trim();
    return p;
  };
  if (isTelco) {
    // Telco mode payload - handle multiple VRFs
    if (values.vrfs && values.vrfs.length > 1) {
      // Multiple VRFs - create array of VRF configurations
      const vrfConfigs = values.vrfs.map((vrf: VRFFormData) => {
        const vrfConfig: VRFClusterConfig = {
          vrfName: vrf.name,
          routeTargets: vrf.routeTargets?.join(", ") || "",
          routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
          nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
          mode: normalizedMode,
          vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
          inboundId: vrf.inboundId || null, // Add inboundId for updates
          persistence: vrf.persistence ?? true, // Hold route on peer down (default true)
          // Next-hop retry; absent when the reflector is below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
          nexthopRetry: buildNexthopSettingsPayload(vrf, values),
        };

        // Handle routing table configuration
        if (vrf.policySelection === "specific") {
          // Filter out invalid routing tables
          vrfConfig.routingTables = validateAndFilterRoutingTables(vrf.routingTables || []);
        } else {
          vrfConfig.routingTagName = vrf.routingTableTagName;
          vrfConfig.routingTagValue = vrf.routingTableTagValue;
        }

        // Handle next-hop configuration
        if (vrf.nextHopSelection === "dynamic_eni_resolver") {
          // Dynamic ENI Resolver: ENI is resolved live from BGP, nothing to send.
          vrfConfig.nextHopType = "DYNAMIC_ENI_RESOLVER";
        } else if (vrf.nextHopSelection === "specific") {
          // Get the selected value (could be ENI or TGW)
          const selectedValue = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : (vrf.selectedEni || "");

          // Determine nextHopType based on prefix
          if (selectedValue.toLowerCase().startsWith("eni")) {
            vrfConfig.nextHopType = "ENI";
            vrfConfig.eni = selectedValue;
          } else if (selectedValue.toLowerCase().startsWith("tgw")) {
            vrfConfig.nextHopType = "TGW";
            vrfConfig.tgw = selectedValue;
          } else {
            // Default to ENI if prefix doesn't match (backward compatibility)
            vrfConfig.nextHopType = "ENI";
            vrfConfig.eni = selectedValue;
          }
        } else {
          vrfConfig.nextHopTagName = vrf.nextHopTagName;
          vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
        }
        
        return vrfConfig;
      });
      
      const telcoPayload: InboundPropagationRuleClusterPayload = {
        mode: normalizedMode,
        vrfs: vrfConfigs,
      };
      if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
        telcoPayload.bgpNeighborIds = bgpNeighborIds;
      }
      return addRuleId(telcoPayload);
    } else {
      // Single VRF - always use vrfs array format for Telco mode
      const vrf = values.vrfs?.[0];
      if (!vrf) {
        throw new Error("No VRF data provided for Telco mode");
      }
      
      const vrfConfig: VRFClusterConfig = {
        vrfName: vrf.name || "Default VRF",
        routeTargets: vrf.routeTargets?.join(", ") || "",
        routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
        nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
        mode: normalizedMode,
        vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
        inboundId: vrf.inboundId || null, // Add inboundId for updates
        persistence: vrf.persistence ?? true, // Hold route on peer down (default true)
        // Next-hop retry; absent when the reflector is below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
        nexthopRetry: buildNexthopSettingsPayload(vrf, values),
      };

        // Handle routing table configuration
        if (vrf.policySelection === "specific") {
          // Filter out invalid routing tables
          vrfConfig.routingTables = validateAndFilterRoutingTables(vrf.routingTables || []);
        } else {
        vrfConfig.routingTagName = vrf.routingTableTagName;
        vrfConfig.routingTagValue = vrf.routingTableTagValue;
      }
      
      // Handle next-hop configuration
      if (vrf.nextHopSelection === "dynamic_eni_resolver") {
        // Dynamic ENI Resolver: ENI is resolved live from BGP, nothing to send.
        vrfConfig.nextHopType = "DYNAMIC_ENI_RESOLVER";
      } else if (vrf.nextHopSelection === "specific") {
        // Get the selected value (could be ENI or TGW)
        const selectedValue = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : (vrf.selectedEni || "");

        // Determine nextHopType based on prefix
        if (selectedValue.toLowerCase().startsWith("eni")) {
          vrfConfig.nextHopType = "ENI";
          vrfConfig.eni = selectedValue;
        } else if (selectedValue.toLowerCase().startsWith("tgw")) {
          vrfConfig.nextHopType = "TGW";
          vrfConfig.tgw = selectedValue;
        } else {
          // Default to ENI if prefix doesn't match (backward compatibility)
          vrfConfig.nextHopType = "ENI";
          vrfConfig.eni = selectedValue;
        }
      } else {
        vrfConfig.nextHopTagName = vrf.nextHopTagName;
        vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
      }
      
      const telcoPayload: InboundPropagationRuleClusterPayload = {
        mode: normalizedMode,
        vrfs: [vrfConfig],
      };
      if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
        telcoPayload.bgpNeighborIds = bgpNeighborIds;
      }
      return addRuleId(telcoPayload);
    }
  } else {
      // Enterprise mode: CREATE and UPDATE both use flat payload (propagationRuleId added for UPDATE)
      if (!propagationRuleId) {
        // CREATE: original flat payload
        const payload: InboundPropagationRuleClusterPayload = {
          mode: normalizedMode,
          routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
          routingTagName: values.policySelection === "dynamic" ? values.routingTableTagName : undefined,
          routingTagValue: values.policySelection === "dynamic" ? values.routingTableTagValue : undefined,
          routingTables: values.policySelection === "specific"
            ? validateAndFilterRoutingTables(values.routingTables || [])
            : undefined,
          nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
          nextHopTagName: values.nextHopSelection === "dynamic" ? values.nextHopTagName : undefined,
          nextHopTagValue: values.nextHopSelection === "dynamic" ? values.nextHopTagValue : undefined,
          persistence: values.persistence ?? true, // Hold route on peer down (default true)
          // Next-hop retry; absent when the reflector is below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
          nexthopRetry: buildNexthopSettingsPayload(values, values),
        };
        if (values.routeTargets && values.routeTargets.length > 0) {
          payload.routeTargets = values.routeTargets.join(", ");
        }
        if (values.selectedVpc) payload.vpcId = values.selectedVpc;
        if (values.nextHopSelection === "dynamic_eni_resolver") {
          payload.nextHopType = "DYNAMIC_ENI_RESOLVER";
        } else if (values.nextHopSelection === "specific") {
          const selectedValue = values.enis && values.enis.length > 0 ? values.enis[0] : (values.selectedEni || "");
          if (selectedValue.toLowerCase().startsWith("eni")) {
            payload.nextHopType = "ENI";
            payload.eni = selectedValue;
          } else if (selectedValue.toLowerCase().startsWith("tgw")) {
            payload.nextHopType = "TGW";
            payload.tgw = selectedValue;
          } else {
            payload.nextHopType = "ENI";
            payload.eni = selectedValue;
          }
        } else {
          payload.nextHopTagName = values.nextHopTagName;
          payload.nextHopTagValue = values.nextHopTagValue;
        }
        if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
          payload.bgpNeighborIds = bgpNeighborIds;
        }
        return addRuleId(payload);
      }

      // UPDATE: flat payload (same level as propagationRuleId), no vrfs wrapper
      const payload: InboundPropagationRuleClusterPayload = {
        mode: normalizedMode,
        routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
        nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
        persistence: values.persistence ?? true, // Hold route on peer down (default true)
        // Next-hop retry; absent when the reflector is below NEXTHOP_RETRY_MIN_VERSION (CTX-181)
        nexthopRetry: buildNexthopSettingsPayload(values, values),
      };
      if (values.routeTargets && values.routeTargets.length > 0) {
        payload.routeTargets = values.routeTargets.join(", ");
      }
      if (values.policySelection === "specific") {
        payload.routingTables = validateAndFilterRoutingTables(values.routingTables || []);
      } else {
        payload.routingTagName = values.routingTableTagName;
        payload.routingTagValue = values.routingTableTagValue;
      }
      if (values.selectedVpc) payload.vpcId = values.selectedVpc;
      if (values.nextHopSelection === "dynamic_eni_resolver") {
        payload.nextHopType = "DYNAMIC_ENI_RESOLVER";
      } else if (values.nextHopSelection === "specific") {
        const selectedValue = values.enis && values.enis.length > 0 ? values.enis[0] : (values.selectedEni || "");
        if (selectedValue.toLowerCase().startsWith("eni")) {
          payload.nextHopType = "ENI";
          payload.eni = selectedValue;
        } else if (selectedValue.toLowerCase().startsWith("tgw")) {
          payload.nextHopType = "TGW";
          payload.tgw = selectedValue;
        } else {
          payload.nextHopType = "ENI";
          payload.eni = selectedValue;
        }
      } else {
        payload.nextHopTagName = values.nextHopTagName;
        payload.nextHopTagValue = values.nextHopTagValue;
      }
      if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
        payload.bgpNeighborIds = bgpNeighborIds;
      }
      return addRuleId(payload);
  }
};

export const buildOutboundPropagationRulesClusterPayload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any[],
  mode = "ENTERPRISE",
  bgpNeighborIds?: number[],
  propagationRuleId?: string,
  defaultRuleName?: string
): OutboundPropagationRulesClusterPayload => {
  const normalizedMode = mode?.toUpperCase() === "TELCO" ? "TELCO" : "ENTERPRISE";
  const payload: OutboundPropagationRulesClusterPayload = {
    mode: normalizedMode,
    outbounds: rules.map((rule) => buildOutboundRuleItemPayload(rule)),
  };
  if (propagationRuleId) payload.propagationRuleId = propagationRuleId;
  if (defaultRuleName != null && String(defaultRuleName).trim() !== "") {
    payload.name = String(defaultRuleName).trim();
  }
  if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
    payload.bgpNeighborIds = bgpNeighborIds;
  }
  return payload;
};

/** @deprecated Use buildOutboundPropagationRulesClusterPayload */
export const buildOutboundPropagationRuleClusterPayload = (
  values: any,
  mode = "ENTERPRISE",
  bgpNeighborIds?: number[],
  propagationRuleId?: string
): OutboundPropagationRuleClusterPayload => {
  const item = buildOutboundRuleItemPayload(values);
  const payload: OutboundPropagationRuleClusterPayload = {
    mode: mode?.toUpperCase() === "TELCO" ? "TELCO" : "ENTERPRISE",
    subnetConfigType: item.subnetConfigType,
    ...(item.name ? { name: item.name } : {}),
    ...(item.subnets ? { subnets: item.subnets } : {}),
    ...(item.subnetTags ? { outboundSubnetTags: item.subnetTags } : {}),
    ...(item.routingTableTags
      ? { outboundRoutingTableTags: item.routingTableTags }
      : {}),
    ...(item.filterSubnetsWithinRoutingTables != null
      ? { filterSubnetsWithinRoutingTables: item.filterSubnetsWithinRoutingTables }
      : {}),
    ...(item.bgpCommunity ? { bgpCommunity: item.bgpCommunity } : {}),
    ...(item.nexthopIPAddress ? { nexthopIPAddress: item.nexthopIPAddress } : {}),
    ...(item.vpcId ? { vpcId: item.vpcId } : {}),
  };
  if (propagationRuleId) payload.propagationRuleId = propagationRuleId;
  if (bgpNeighborIds != null && bgpNeighborIds.length > 0) {
    payload.bgpNeighborIds = bgpNeighborIds;
  }
  return payload;
};

interface ClusterInstance {
    clusterId: number;
    reflectors: number[];
}

export interface ReflectorBgpConfig {
    localAs: number;
    routerId?: string;
    routerId1?: string;
    routerId2?: string;
}
interface ReflectorRole {
    reflectorId: number;
    role: string;
    interfaceAddressId?: number;
}

interface Cluster {
    name?: string;
    description: string;
    region: string;
    cloud: string;
    status: string;
    reflectors: ReflectorRole[];
}

/**GET /clusters
  Gets all clusters for the authenticated tenant with their associated instances.
 */
export function getClusters(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v2/reflector/clusters?tenantId=${tenantId}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**
 * GET /clusters/:id/bgp-neighbors
 * Gets list of BGP neighbors for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getClusterBgpNeighbors(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-neighbors/grouped?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**GET /clusters/:id
Gets specific cluster details with its instances.
 * @param token - Authentication token
 * @param id - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getClusterById(token: string, id: string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${id}?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**
 * GET /cloudelements/clusters/:idCluster
 * Gets cloud elements for a specific cluster with pagination.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @param filter - Optional search filter value
 */
export function getClusterCloudElements(token: string, clusterId: string, tenantId: number, page = 1, limit = 20, filter?: string) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString(),
        page: page.toString(),
        limit: limit.toString()
    });
    if (filter && filter.trim() !== '') {
        params.append('filter', filter.trim());
    }
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**GET /cloudelements/clusters/:clusterId/paginated
 * Gets cloud elements for a specific cluster with pagination.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @param filter - Optional search filter value
 */
export function getClusterCloudElementsPaginated(token: string, clusterId: string, tenantId: number, page = 1, limit = 20, filter?: string) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString(),
        page: page.toString(),
        limit: limit.toString()
    });
    if (filter && filter.trim() !== '') {
        params.append('filter', filter.trim());
    }
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}/paginated?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST /clusters/add-reflector
  Creates a new cluster with the given reflectors.
 * @param token - Authentication token
 * @param payload - Cluster instance payload
 * @param tenantId - Tenant ID
 */
export function addReflectorsToCluster(token: string, payload: ClusterInstance, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/clusters/add-reflector?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST reflector/clusters
 * Creates a new cluster with the given reflectors.
 * @param token - Authentication token
 * @param payload - Cluster payload
 * @param tenantId - Tenant ID
 */
export function createCluster(token: string, payload: Cluster, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/clusters?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/** PUT v2/reflector/clusters/:id/name 
 * Updates the name of a specific Cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - Name payload
 * @param tenantId - Tenant ID
*/
export function updateClusterName(token: string, clusterId: number, payload: { name: string }, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/name?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:id/tags
 * Updates the tags of a specific Cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - Tags payload
 * @param tenantId - Tenant ID
 */
export function updateClusterTags(token: string, clusterId: number, payload: { tags: string[] }, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/tags?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:clusterId/update-roles 
 * Updates the roles of the reflectors in a cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - Roles payload
 * @param tenantId - Tenant ID
 */
export function updateClusterRoles(token: string, clusterId: number, payload: { roles: ReflectorRole[] }, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/update-roles?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**DELETE /v2/reflector/clusters/:clusterId
  Dissolves a cluster and removes all associated reflectors.
  @param token - Authentication token
  @param clusterId - Cluster ID
  @param tenantId - Tenant ID
  @param neighbors - If true, also deletes BGP configurations and Cloud Propagation
 */
export function dissolveCluster(token: string, clusterId: number, tenantId: number, neighbors = false) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    if (neighbors) {
        params.append('neighbors', 'true');
    }
    return axios.delete(`${API_URL}/v2/reflector/clusters/${clusterId}?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**GET /v2/reflector/clusters/:clusterId/iam-permissions
 * Gets the IAM permissions for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getClusterIamPermissions(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/iam-permissions?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/*GET v2/reflector/instances/:id/interfaces/addresses
 * Gets the interfaces addresses for a specific reflector.
 * @param token - Authentication token
 * @param reflectorId - Reflector ID
 * @param tenantId - Tenant ID
 */
export function getInterfacesAddresses(token: string, reflectorId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/interfaces/addresses?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

//BGP CONFIGURATION

/**GET /v2/reflector/clusters/:id/bgp-config
 * Gets the BGP configuration for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getBgpConfig(token: string, clusterId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config?${params.toString()}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST /v2/reflector/clusters/:id/bgp-config
 * Creates a new BGP configuration for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - BGP configuration payload
 * @param tenantId - Tenant ID
 */
export function createBgpConfig(token: string, clusterId: number, payload: ReflectorBgpConfig, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:id/bgp-config
 * Updates the BGP configuration for a specific cluster.
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - BGP configuration payload
 * @param tenantId - Tenant ID
 */
export function updateBgpConfig(token: string, clusterId: number, payload: ReflectorBgpConfig, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config?${params.toString()}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}



//CLOUD PROPAGATION

/**
 * GET /v2/reflector/clusters/:clusterId/propagation-rules
 * Gets cloud propagation rules for cluster
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 */
export function getCloudPropagationRulesByCluster(token: string, clusterId:number, tenantId: number){
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/grouped?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * CREATE INBOUND Propagation Rule Cluster
 * POST v2/reflector/clusters/:clusterId/propagation-rules/inbound
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - Inbound propagation rule payload
 * @param tenantId - Tenant ID
 */
export function createInboundPropagationRuleCluster(token: string, clusterId: number, payload: InboundPropagationRuleClusterPayload, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/inbound?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * CREATE OUTBOUND Propagation Rule Cluster
 * POST v2/reflector/clusters/:clusterId/propagation-rules/outbound
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param payload - Outbound propagation rule payload
 * @param tenantId - Tenant ID
 */
export function createOutboundPropagationRuleCluster(token: string, clusterId: number, payload: OutboundPropagationRulesClusterPayload, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/outbound?${params.toString()}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT /v2/reflector/clusters/:clusterId/propagation-rules/:propagationRuleId/inbounds
 * Update inbound propagation rule for cluster (propagationRuleId is UUID).
 * Ensures propagationRuleId is sent in the request body for the update.
 */
export function updateInboundPropagationRuleCluster(token: string, clusterId: number, propagationRuleId: number | string, payload: Partial<InboundPropagationRuleClusterPayload>, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    const ruleIdStr = String(propagationRuleId);
    const { inboundId: _dropped, ...rest } = payload;
    void _dropped;
    const body = { ...rest, propagationRuleId: ruleIdStr };
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/${ruleIdStr}/inbounds?${params.toString()}`, body, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT /v2/reflector/clusters/:clusterId/propagation-rules/:propagationRuleId/outbounds
 * Update all outbound propagation rules in one request
 */
export function updateOutboundPropagationRulesCluster(
  token: string,
  clusterId: number,
  propagationRuleId: number | string,
  payload: Partial<OutboundPropagationRulesClusterPayload>,
  tenantId: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
  });
  const ruleIdStr = String(propagationRuleId);
  const body = { ...payload, propagationRuleId: ruleIdStr };
  return axios.put(
    `${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/${ruleIdStr}/outbounds?${params.toString()}`,
    body,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * PUT /v2/reflector/clusters/:clusterId/propagation-rules/:propagationRuleId/outbound/:outboundId
 * @deprecated Per-rule update — prefer updateOutboundPropagationRulesCluster with outbounds[]
 */
export function updateOutboundPropagationRuleCluster(
  token: string,
  clusterId: number,
  propagationRuleId: number | string,
  outboundId: number,
  payload: Partial<OutboundPropagationRuleClusterPayload>,
  tenantId: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
  });
  const ruleIdStr = String(propagationRuleId);
  const body = { ...payload, propagationRuleId: ruleIdStr };
  return axios.put(
    `${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/${ruleIdStr}/outbound/${outboundId}?${params.toString()}`,
    body,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * DELETE /v2/reflector/propagation-rules/:propagationRuleId/inbound/:inboundId
 * Deletes a single VRF (inbound) for cluster mode. There is no cluster-scoped
 * DELETE route — the rule is looked up by its UUID (cluster-agnostic), so this
 * reuses the same endpoint as the standalone delete.
 */
export function deleteInboundPropagationRuleCluster(token: string,  propagationRuleId: number | string, inboundId: number, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}/inbound/${inboundId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * DELETE /v2/reflector/propagation-rules/:propagationRuleId/outbound/:outboundId
 * Deletes a single outbound rule for cluster mode. There is no cluster-scoped
 * DELETE route — the rule is looked up by its UUID (cluster-agnostic), so this
 * reuses the same endpoint as the standalone delete (see CTX-154).
 */
export function deleteOutboundPropagationRuleCluster(
  token: string,
  propagationRuleId: number | string,
  outboundId: number,
  tenantId: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
  });
  const ruleIdStr = String(propagationRuleId);
  return axios.delete(
    `${API_URL}/v2/reflector/propagation-rules/${ruleIdStr}/outbound/${outboundId}?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

/**
 * DELETE /v2/reflector/clusters/:clusterId/propagation-rules/{UUID}
 * Deletes the entire propagation rule in cluster context.
 */
export function deletePropagationRuleCluster(token: string, propagationRuleId: number | string, tenantId: number) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_URL}/v2/reflector/propagation-rules/${propagationRuleId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
